import { Fragment, useMemo } from 'react';
import { generateHTML } from '@tiptap/core';

import {
  BASE_FILL_IN_BLANKS_TEXT_BLOCK_SETTINGS,
  FillInBlanksAnswer,
  FillInBlanksSentence as FillInBlanksSentenceType,
  FillInBlanksText,
} from 'approot/activity-builder/components/fill-in-blanks/fill-in-blanks.constants';
import { mergeFillInBlanksBlocks } from 'approot/activity-builder/components/fill-in-blanks/logic/merge-fill-in-blanks-blocks';
import { SettingsGroup } from 'approot/activity-builder/settings/settings.style';
import { sortFillInBlanksSentences } from 'approot/activity-builder/components/fill-in-blanks/logic/sort-fill-in-blanks-sentences';
import { ComponentFillInBlanks } from 'approot/activity-builder/components/component.constants';
import type { SuggestedAnswerFragment } from '../../suggested-answers.graphql.generated';
import { getRichtextAnswer } from 'approot/activity-builder/components/shared/logic/get-richtext-answer';
import {
  getRichtextValue,
  isRichtextValueEmpty,
} from 'approot/activity-builder/components/shared/logic/get-richtext-value';
import { getExtensions } from 'approot/shared/richtext/richtext.utils';
import { SUGGESTED_ANSWER_FILL_IN_BLANKS_SETTINGS } from '../../suggested-answer.constants';
import * as Styled from './suggested-answer-fill-in-blanks-view.style';
import type { Pluck } from 'lib/gql/pluck';

type AnswerMap = {
  [key: string]: Pluck<SuggestedAnswerFragment, 'suggestedAnswers'> | undefined;
};

type CommonProps = {
  suggestedAnswers: AnswerMap;
};

const AnswerBlock = ({
  answer,
  suggestedAnswers,
}: CommonProps & {
  answer: FillInBlanksAnswer;
}) => {
  const suggestedAnswerRichtext = getRichtextAnswer(
    suggestedAnswers[answer.itemId]
  );
  const hasAnswer = !isRichtextValueEmpty(suggestedAnswerRichtext);
  return hasAnswer ? (
    <Styled.SuggestedAnswerFillInBlanksViewAnswerOuter>
      {answer.punctuation === '"' ? <span>“</span> : ''}
      <Styled.SuggestedAnswerFillInBlanksViewAnswer
        id={answer.itemId}
        dangerouslySetInnerHTML={{
          __html: generateHTML(
            suggestedAnswerRichtext,
            getExtensions(SUGGESTED_ANSWER_FILL_IN_BLANKS_SETTINGS)
          ),
        }}
      />
      {answer.punctuation ? (
        <span>{answer.punctuation === '"' ? '”' : answer.punctuation}</span>
      ) : (
        ''
      )}
    </Styled.SuggestedAnswerFillInBlanksViewAnswerOuter>
  ) : (
    <Styled.SuggestedAnswerFillInBlanksViewAnswerOuter>
      {answer.punctuation === '"' ? <span>“</span> : ''}
      <Styled.SuggestedAnswerFillInBlanksViewAnswerEmpty />
      {answer.punctuation ? (
        <span>{answer.punctuation === '"' ? '”' : answer.punctuation}</span>
      ) : (
        ''
      )}
    </Styled.SuggestedAnswerFillInBlanksViewAnswerOuter>
  );
};

const TextBlock = ({ textBlock }: { textBlock: FillInBlanksText }) => {
  const richtext = useMemo(() => {
    return getRichtextValue(
      textBlock.text,
      getExtensions(BASE_FILL_IN_BLANKS_TEXT_BLOCK_SETTINGS)
    );
  }, [textBlock.text]);

  return (
    <Styled.SuggestedAnswerFillInBlanksText
      key={textBlock.itemId}
      dangerouslySetInnerHTML={{ __html: richtext }}
    />
  );
};

const Sentence = ({
  sentence,
  suggestedAnswers,
}: CommonProps & {
  sentence: FillInBlanksSentenceType;
}) => {
  const mergedBlocks = useMemo(
    () => mergeFillInBlanksBlocks(sentence),
    [sentence]
  );

  return mergedBlocks.map(block => (
    <Fragment key={block.itemId}>
      {'text' in block ? (
        <TextBlock key={block.itemId} textBlock={block} />
      ) : (
        <AnswerBlock
          key={`${block.itemId}-${block.showPlaceholderAsScaffold}-${block.placeholder}`}
          answer={block}
          suggestedAnswers={suggestedAnswers}
        />
      )}
    </Fragment>
  ));
};

const SuggestedAnswerFillInBlanksViewFields = (
  component: ComponentFillInBlanks
) => {
  const { properties, suggestedAnswerProperties } = component;

  const sortedSentences = useMemo(
    () => sortFillInBlanksSentences(properties.fillInBlanks.sentences),
    [properties.fillInBlanks.sentences]
  );

  return (
    <SettingsGroup marginBottom={0}>
      {sortedSentences.map(sentence => {
        const suggestedAnswers: AnswerMap = {};
        sentence.answerBlocks.forEach(block => {
          const answer = suggestedAnswerProperties?.suggestedAnswers?.find(
            answer => answer.answerReferences?.id === block.itemId
          );
          suggestedAnswers[block.itemId] = answer;
        });
        return (
          <Styled.SuggestedAnswerFillInBlanksViewSentence key={sentence.itemId}>
            <Sentence sentence={sentence} suggestedAnswers={suggestedAnswers} />
          </Styled.SuggestedAnswerFillInBlanksViewSentence>
        );
      })}
    </SettingsGroup>
  );
};

export default SuggestedAnswerFillInBlanksViewFields;
